/** @jsx isolateComponent */

import { Fragment, useState } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import { formatDateTime } from '@zapier/date';
import { Button, Link, Spacer } from '@zapier/design-system';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import { TrialExtensionPaymentMethodModal } from '@zapier/design-system-revenue/src/components/other/TrialExtensionPaymentMethodModal';
import {
  TrialExtensionReason,
  trialExtensionReasons,
} from '@zapier/design-system-revenue/src/types';
import {
  buildUpgradeUrl,
  useCurrentPlan,
} from '@zapier/design-system-revenue/src/utils';

import { Layout } from './Layout';
import { NudgeImage } from './NudgeImage';

type UpgradeReasonMessagingProps = {
  appName?: string;
  canExtendTrial: boolean;
  plan: any;
};

const upgradeReasonMessagingProps = ({
  appName,
  canExtendTrial,
  plan,
}: UpgradeReasonMessagingProps) => ({
  [trialExtensionReasons.premium]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use unlimited Premium Apps.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: 'Premium App',
    image: (
      <NudgeImage appName={appName} type={trialExtensionReasons.premium} />
    ),
    subtitleText: 'Paid Feature',
  },
  [trialExtensionReasons.premiumLimit]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need more{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use unlimited Premium Apps.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to unlimited{' '}
        <Link href="/apps/categories/premium" target="_blank">
          Premium Apps
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: "You've run out of Premium Apps",
    image: (
      <NudgeImage appName={appName} type={trialExtensionReasons.premiumLimit} />
    ),
    subtitleText: 'Paid Feature',
  },
  [trialExtensionReasons.zapLimit]: {
    bodyText: canExtendTrial
      ? "To turn on your Zap, you'll need access to more Zaps. Upgrade now or start a free 7-day trial of the Professional plan to use unlimited Zaps."
      : "To turn on your Zap, you'll need access to more Zaps. Turn off some of your other Zaps or upgrade now to turn on this Zap.",
    headingText: 'More Zaps',
    image: <NudgeImage type={trialExtensionReasons.zapLimit} />,
    subtitleText: 'Heads Up',
  },
  [trialExtensionReasons.multistep]: {
    bodyText: canExtendTrial
      ? `To create a Zap with more than one action, you'll need a paid plan. Upgrade now or start a free 7-day trial of the Professional plan to use multi-step Zaps.`
      : "To create a Zap with more than one action, you'll need a paid plan. Edit your zap or upgrade now to turn on your Zap.",
    headingText: 'Multi-step Zap',
    image: <NudgeImage type={trialExtensionReasons.multistep} />,
    subtitleText: 'Paid Feature',
  },
  [trialExtensionReasons.paths]: {
    bodyText: canExtendTrial ? (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link
          href="/help/create/customize/add-branching-logic-to-zaps-with-paths"
          target="_blank"
        >
          Paths
        </Link>
        . Upgrade now or start a free 7-day trial of the Professional plan to
        use Paid Features like Paths.
      </Fragment>
    ) : (
      <Fragment>
        To turn on your Zap, you'll need access to{' '}
        <Link
          href="/help/create/customize/add-branching-logic-to-zaps-with-paths"
          target="_blank"
        >
          Paths
        </Link>
        . Edit your Zap or upgrade now to turn on your Zap.
      </Fragment>
    ),
    headingText: 'Paths',
    image: <NudgeImage type={trialExtensionReasons.paths} />,
    subtitleText: 'Paid Feature',
  },
  [trialExtensionReasons.taskLimit]: {
    bodyText: canExtendTrial
      ? `To activate your Zap, you'll need more Tasks. Upgrade now or start a free 7-day trial of Professional for ${Number(
          plan.limits.action_limit
        ).toLocaleString()} tasks.`
      : "To activate your Zap, you'll need more Tasks. Reduce the number of Zaps you're using or upgrade now to activate.",
    headingText: 'More Tasks',
    image: <NudgeImage type={trialExtensionReasons.taskLimit} />,
    subtitleText: 'Paid Feature',
  },
  [trialExtensionReasons.emailSupport]: {
    bodyText: (
      <Fragment>
        Upgrade to email our world class Support team and we’ll help get you up
        & running! Or, get{' '}
        <Link href="/community" target="_blank">
          support from our Community
        </Link>
        , for free.
      </Fragment>
    ),
    headingText: 'Upgrade to contact Support',
    image: <NudgeImage type={trialExtensionReasons.emailSupport} />,
    subtitleText: 'Paid Feature',
  },
  [trialExtensionReasons.unknown]: {
    bodyText: "To activate your Zap, you'll need to upgrade your account.",
    headingText: 'Upgrade Needed',
  },
});

type UpgradeReasonProps = {
  appName?: string;
  canExtendTrial: boolean;
  onBack: () => void;
  onExtendTrial: () => void;
  onUpgradeClick: () => void;
  plan: any;
  reason: TrialExtensionReason;
};

export const UpgradeReason = ({
  appName,
  canExtendTrial,
  onBack,
  onExtendTrial,
  onUpgradeClick,
  plan,
  reason,
}: UpgradeReasonProps) => {
  const { currentPlan, isLoading: isLoadingCurrentPlan } = useCurrentPlan();
  const [
    isShowingTrialExtensionPaymentMethodModal,
    setIsShowingTrialExtensionPaymentMethodModal,
  ] = useState(false);

  const EVENT_NAME = 'editor trial extension activated';
  const { trackEvent } = useTrackingContext();
  const sevenDaysFromToday = new Date().setDate(new Date().getDate() + 7);

  const monthlyAmount =
    plan.interval === 'year' ? plan.amount / 12 : plan.amount;

  const eventProperties = {
    planName: plan.name,
    price: monthlyAmount,
    renewalDate: formatDateTime(sevenDaysFromToday, 'iso'),
    upgradeUrl: buildUpgradeUrl(
      currentPlan?.name || '',
      plan.name,
      plan.interval
    ),
  };

  const ExtendTrialButton = () => (
    <Fragment>
      <Button
        casing="uppercase"
        color="greyAlt"
        key="extend"
        onClick={() => setIsShowingTrialExtensionPaymentMethodModal(true)}
      >
        Start Trial
      </Button>

      {isShowingTrialExtensionPaymentMethodModal && (
        <TrialExtensionPaymentMethodModal
          monthlyAmount={monthlyAmount}
          onClose={onBack}
          onContinue={() => {
            trackEvent(EVENT_NAME, eventProperties);
            onExtendTrial();
          }}
          trialEndDate={sevenDaysFromToday}
          trialPlanName={plan.name}
        />
      )}
    </Fragment>
  );

  const messagingProps = upgradeReasonMessagingProps({
    appName,
    canExtendTrial,
    plan,
  })[reason || trialExtensionReasons.unknown];

  const contentProps = {
    ...messagingProps,

    buttons: [
      <Button
        casing="uppercase"
        hasShadow={true}
        key="upgrade"
        onClick={() => onUpgradeClick()}
      >
        Upgrade Now
      </Button>,

      <Spacer key="space" width={10} />,

      !isLoadingCurrentPlan ? (
        canExtendTrial ? (
          <ExtendTrialButton key="extend-trial" />
        ) : (
          <Button
            casing="uppercase"
            color="greyAlt"
            key="close"
            onClick={onBack}
          >
            Back
          </Button>
        )
      ) : null,
    ],

    subtitleText: reason ? messagingProps.subtitleText : null,
  };

  return <Layout {...contentProps} />;
};
